import { Injectable, signal } from '@angular/core';
import {
  NEUTRAL_CHILD_STORAGE_VALUE,
  STORAGE_KEY_SELECTED_CHILD,
} from '../parent-ai-assistant/constants/topic-category.constants';

@Injectable({ providedIn: 'root' })
export class ParentFamilyContextService {
  /** Shared kid scope for Valvur Assistant, Patrol, and family bar (null = general parenting). */
  readonly selectedChildId = signal<string | null>(null);

  selectChild(childId: string): void {
    if (this.selectedChildId() === childId) {
      return;
    }
    this.selectedChildId.set(childId);
    this.persistSelectedChildId(childId);
  }

  selectNeutral(): void {
    if (this.selectedChildId() === null) {
      return;
    }
    this.selectedChildId.set(null);
    this.persistSelectedChildId(null);
  }

  getStoredChildId(linkedKids: readonly { id: string }[]): string | null {
    try {
      let stored = localStorage.getItem(STORAGE_KEY_SELECTED_CHILD);
      if (!stored) {
        stored = sessionStorage.getItem(STORAGE_KEY_SELECTED_CHILD);
        if (stored) {
          localStorage.setItem(STORAGE_KEY_SELECTED_CHILD, stored);
          sessionStorage.removeItem(STORAGE_KEY_SELECTED_CHILD);
        }
      }
      if (!stored || stored === NEUTRAL_CHILD_STORAGE_VALUE) {
        return null;
      }
      return linkedKids.some((k) => k.id === stored) ? stored : null;
    } catch {
      return null;
    }
  }

  isNeutralChildSelectionStored(): boolean {
    try {
      const raw =
        localStorage.getItem(STORAGE_KEY_SELECTED_CHILD) ??
        sessionStorage.getItem(STORAGE_KEY_SELECTED_CHILD);
      return raw === NEUTRAL_CHILD_STORAGE_VALUE;
    } catch {
      return false;
    }
  }

  persistSelectedChildId(childId: string | null): void {
    try {
      localStorage.setItem(
        STORAGE_KEY_SELECTED_CHILD,
        childId ?? NEUTRAL_CHILD_STORAGE_VALUE,
      );
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * Restore stored kid / neutral, or default first child. Updates signal only (no AI side effects).
   * Returns true when selection changed.
   */
  syncInitialSelection(
    linkedKids: readonly { id: string }[],
    options: { verificationMode: boolean },
  ): boolean {
    if (linkedKids.length === 0) {
      if (options.verificationMode && this.selectedChildId() !== null) {
        this.selectedChildId.set(null);
      }
      return false;
    }

    const current = this.selectedChildId();
    if (current && linkedKids.some((k) => k.id === current)) {
      return false;
    }

    if (current === null && this.isNeutralChildSelectionStored()) {
      return false;
    }

    const storedKid = this.getStoredChildId(linkedKids);
    if (storedKid) {
      this.selectedChildId.set(storedKid);
      this.persistSelectedChildId(storedKid);
      return true;
    }

    const firstId = linkedKids[0]!.id;
    this.selectedChildId.set(firstId);
    this.persistSelectedChildId(firstId);
    return true;
  }
}
